import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * CityAutocomplete (IBGE + extraItems do Firestore)
 * - Filtra sem acento e sem case
 * - strict=true: só permite valores que existam na lista (IBGE + extras)
 * - extraItems: array de strings (ex.: "Trevo da Facão - TO")
 *
 * Props:
 *  - value (string)
 *  - onSelect (fn) ou onChange (fn) [fallback]
 *  - placeholder (string)
 *  - strict (bool)
 *  - extraItems (string[])
 */
function normalizeText(str = "") {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function canonicalLabel(str = "") {
  // padroniza "Cidade-TO" -> "Cidade - TO"
  let s = String(str || "").replace(/\s+/g, " ").trim();
  if (!s) return "";

  // se tiver UF no fim, normaliza separador
  // exemplos aceitos: "Palmas-TO", "Palmas - TO", "Palmas , TO"
  const m = s.match(/^(.*?)(?:\s*[-,]\s*)([A-Za-z]{2})$/);
  if (m) {
    const name = m[1].trim();
    const uf = m[2].toUpperCase();
    return name ? `${name} - ${uf}` : `${uf}`;
  }
  return s;
}

export default function CityAutocomplete({
  placeholder,
  onSelect,
  onChange,
  value,
  strict = false,
  extraItems = [],
}) {
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const justPickedRef = useRef(false);
  const lastValidRef = useRef(canonicalLabel(value || ""));

  const [allCities, setAllCities] = useState([]); // [{label, norm}]
  const [inputValue, setInputValue] = useState(canonicalLabel(value || ""));
  const [suggestions, setSuggestions] = useState([]); // string[]
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // --- carrega IBGE (cache local) ---
  useEffect(() => {
    let mounted = true;

    const fetchCities = async () => {
      try {
        const cacheKey = "ibge_cities_cache_v3";
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            if (mounted) setAllCities(parsed);
            return;
          }
        }

        const res = await fetch("https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome");
        const data = await res.json();

        const formatted = (Array.isArray(data) ? data : [])
          .map((c) => {
            const nome = c?.nome || "";
            const uf = c?.microrregiao?.mesorregiao?.UF?.sigla || "";
            const label = canonicalLabel(uf ? `${nome} - ${uf}` : nome);
            return label ? { label, norm: normalizeText(label) } : null;
          })
          .filter(Boolean);

        localStorage.setItem(cacheKey, JSON.stringify(formatted));
        if (mounted) setAllCities(formatted);
      } catch (err) {
        console.error("Erro ao carregar cidades IBGE:", err);
        const fallback = ["Araguaína - TO", "Palmas - TO", "Goiânia - GO"].map((label) => ({
          label,
          norm: normalizeText(label),
        }));
        if (mounted) setAllCities(fallback);
      }
    };

    fetchCities();

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setActiveIndex(-1);

        // valida ao sair (strict)
        if (strict) {
          const canon = canonicalLabel(inputValue);
          const ok = mergedLabelsSetRef.current.has(normalizeText(canon));
          if (!ok) {
            // volta para último válido (ou limpa)
            const back = lastValidRef.current || "";
            setInputValue(back);
            callSelect(back);
          }
        }
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () => {
      mounted = false;
      document.removeEventListener("pointerdown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- merge IBGE + extraItems (sem duplicar) ---
  const merged = useMemo(() => {
    const base = Array.isArray(allCities) ? allCities : [];
    const extras = (Array.isArray(extraItems) ? extraItems : [])
      .map((s) => canonicalLabel(s))
      .filter((s) => s && s.length > 0);

    const seen = new Set(base.map((c) => c.norm));
    const out = [...base];

    for (const e of extras) {
      const norm = normalizeText(e);
      if (!norm) continue;
      if (seen.has(norm)) continue;
      seen.add(norm);
      out.push({ label: e, norm });
    }

    // ordena por label (bonito)
    out.sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
    return out;
  }, [allCities, extraItems]);

  // set rápido p/ validação no blur/outside
  const mergedLabelsSetRef = useRef(new Set());
  useEffect(() => {
    mergedLabelsSetRef.current = new Set(merged.map((c) => c.norm));
  }, [merged]);

  // --- mantém input sincronizado com value do pai (controlado) ---
  useEffect(() => {
    const canon = canonicalLabel(value || "");

    // se acabamos de escolher, não sobrescreve em re-render imediato
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return;
    }

    setInputValue(canon);

    if (!strict) {
      lastValidRef.current = canon;
      return;
    }

    // strict: só atualiza lastValid se for válido (ou vazio)
    const ok = !canon || mergedLabelsSetRef.current.has(normalizeText(canon));
    if (ok) lastValidRef.current = canon;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // --- callbacks ---
  const callSelect = (val) => {
    if (typeof onSelect === "function") return onSelect(val);
    if (typeof onChange === "function") return onChange(val);
  };

  const runFilter = (val) => {
    const q = normalizeText(val);
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveIndex(-1);
      return;
    }

    const filtered = merged
      .filter((city) => city.norm.includes(q))
      .slice(0, 10)
      .map((city) => city.label);

    setSuggestions(filtered);
    setShowSuggestions(true);
    setActiveIndex(-1);
  };

  const handleChangeInput = (e) => {
    const valRaw = e.target.value;
    const val = valRaw; // não canoniza durante digitação para não “pular” cursor
    setInputValue(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runFilter(val), 150);
  };

  const handlePick = (s) => {
    const canon = canonicalLabel(s);
    justPickedRef.current = true;

    setInputValue(canon);
    lastValidRef.current = canon;

    callSelect(canon);

    setShowSuggestions(false);
    setActiveIndex(-1);
  };

  const validateStrictNow = () => {
    if (!strict) return;

    const canon = canonicalLabel(inputValue);
    const ok = !canon || mergedLabelsSetRef.current.has(normalizeText(canon));
    if (!ok) {
      const back = lastValidRef.current || "";
      setInputValue(back);
      callSelect(back);
    } else {
      // garante canonical
      if (canon !== inputValue) setInputValue(canon);
      lastValidRef.current = canon;
      callSelect(canon);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // se tem highlight, escolhe
      if (showSuggestions && suggestions.length > 0 && activeIndex >= 0) {
        e.preventDefault();
        handlePick(suggestions[activeIndex]);
        return;
      }
      // strict: valida no enter
      if (strict) {
        e.preventDefault();
        validateStrictNow();
      }
      return;
    }

    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setActiveIndex(-1);
      if (strict) validateStrictNow();
    }
  };

  return (
    <div ref={wrapperRef} className="autocomplete-container" style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChangeInput}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // blur acontece antes do click no item; deixamos o click resolver
          // então só valida se NÃO estiver com dropdown aberto
          setTimeout(() => {
            if (!wrapperRef.current) return;
            const hasFocusInside = wrapperRef.current.contains(document.activeElement);
            if (hasFocusInside) return;
            validateStrictNow();
          }, 0);
        }}
        onFocus={() => {
          if (inputValue && inputValue.length > 1) runFilter(inputValue);
        }}
        autoComplete="off"
        className="autocomplete-input"
        style={{ width: "100%" }}
      />

      {showSuggestions && suggestions.length > 0 && (
        <ul
          className="autocomplete-dropdown"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "#ffffff",
            color: "#0f172a",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            zIndex: 9999,
            listStyle: "none",
            padding: 6,
            margin: "6px 0 0 0",
            boxShadow: "0 14px 30px rgba(0,0,0,0.18)",
            maxHeight: "240px",
            overflowY: "auto",
          }}
        >
          {suggestions.map((s, i) => {
            const active = i === activeIndex;
            return (
              <li
                key={`${normalizeText(s)}-${i}`}
                onMouseDown={(e) => {
                  // evita blur limpar antes do pick
                  e.preventDefault();
                }}
                onClick={() => handlePick(s)}
                onMouseEnter={() => setActiveIndex(i)}
                style={{
                  padding: "10px 12px",
                  cursor: "pointer",
                  borderBottom: i === suggestions.length - 1 ? "none" : "1px solid #f1f5f9",
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0f172a",
                  background: active ? "rgba(99,102,241,0.12)" : "transparent",
                  borderRadius: 8,
                }}
              >
                {s}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
